import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { refreshSession } from './api/client.js';
import { useAuthStore } from './auth/store.js';
import { AppShell } from './shell/AppShell.js';
import { LandingPage } from './screens/LandingPage.js';
import { LoginPage } from './screens/LoginPage.js';
import { AdminOverviewPage } from './screens/AdminOverviewPage.js';
import { WorkspacesPage } from './screens/WorkspacesPage.js';
import { AssetsPage } from './screens/AssetsPage.js';
import { AssetDetailPage } from './screens/AssetDetailPage.js';
import { ImportPage } from './screens/ImportPage.js';
import { ConnectorsPage } from './screens/ConnectorsPage.js';
import { PreviewPage } from './screens/PreviewPage.js';
import { CampaignsPage } from './screens/CampaignsPage.js';
import { CampaignBuilderPage } from './screens/CampaignBuilderPage.js';
import { CampaignDetailPage } from './screens/CampaignDetailPage.js';
import { CreativePage } from './screens/CreativePage.js';
import { HomePage } from './screens/HomePage.js';
import { DashboardPage } from './screens/DashboardPage.js';
import { WeeklyReviewPage } from './screens/WeeklyReviewPage.js';
import { ExperimentsPage } from './screens/ExperimentsPage.js';
import { BrandSafetyPage } from './screens/BrandSafetyPage.js';
import { OptimisePage } from './screens/OptimisePage.js';
import { AttributionPage } from './screens/AttributionPage.js';
import { ReportsPage } from './screens/ReportsPage.js';
import { PublicReportPage } from './screens/PublicReportPage.js';
import { LocalizationPage } from './screens/LocalizationPage.js';
import { WebhooksPage } from './screens/WebhooksPage.js';
import { AiAssistantPage } from './screens/AiAssistantPage.js';
import { ChannelStandardsPage } from './screens/ChannelStandardsPage.js';
import { BillingPage } from './screens/BillingPage.js';
import { BrandingPage } from './screens/BrandingPage.js';
import { SecurityPage } from './screens/SecurityPage.js';
import { DemoPage } from './screens/DemoPage.js';
import { EngagePage } from './screens/EngagePage.js';

/**
 * Routes and the session bootstrap (§17.3).
 *
 * On load the access token is gone (it was only ever in memory, §21.4), so the
 * app attempts one silent refresh against the httpOnly cookie before deciding
 * whether the visitor is signed in. Rendering the login screen first and then
 * redirecting would flash a spurious sign-in prompt at an authenticated user.
 */
export function App() {
  const { session, bootstrapping, setBootstrapped } = useAuthStore();

  useEffect(() => {
    let cancelled = false;
    void refreshSession().finally(() => {
      if (!cancelled) setBootstrapped();
    });
    return () => {
      cancelled = true;
    };
  }, [setBootstrapped]);

  if (bootstrapping) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-muted">
        <p className="font-heading text-navy-700">Restoring session…</p>
      </div>
    );
  }

  if (session === null) {
    return (
      <Routes>
        {/* Public front door: the product story precedes the sign-in gate. */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/login" element={<LoginPage />} />
        {/* Public client portal: a shared report opens with a token, no login. */}
        <Route path="/r/:token" element={<PublicReportPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // One sign-in, role-based destination (§6). The credentials made the
  // difference upstream: a platform admin's session carries platformRole
  // 'admin', and everyone else is a client scoped to their own workspaces.
  const isPlatformAdmin = session.platformRole === 'admin';

  return (
    <Routes>
      {/* Estate overview lives OUTSIDE the workspace shell: it is cross-org, so
          the workspace sidebar does not fit it. Guarded client-side here and,
          authoritatively, by requirePlatformAdmin on the API. */}
      <Route
        path="/admin"
        element={isPlatformAdmin ? <AdminOverviewPage /> : <Navigate to="/workspaces" replace />}
      />
      {/* The public report portal renders outside the shell for a signed-in user too. */}
      <Route path="/r/:token" element={<PublicReportPage />} />
      <Route
        path="*"
        element={
          <AppShell>
            <Routes>
              <Route
                path="/"
                element={<Navigate to={isPlatformAdmin ? '/admin' : '/workspaces'} replace />}
              />
              <Route path="/workspaces" element={<WorkspacesPage />} />
              {/* Organisation-level settings live outside the workspace path:
                  branding is a property of the whole org, not one workspace. */}
              <Route path="/settings/branding" element={<BrandingPage />} />
              <Route path="/settings/security" element={<SecurityPage />} />
              <Route path="/w/:workspaceId/assets" element={<AssetsPage />} />
              <Route path="/w/:workspaceId/assets/:assetId" element={<AssetDetailPage />} />
              <Route path="/w/:workspaceId/import" element={<ImportPage />} />
              <Route path="/w/:workspaceId/connectors" element={<ConnectorsPage />} />
              <Route path="/w/:workspaceId/engage" element={<EngagePage />} />
              <Route path="/w/:workspaceId/preview" element={<PreviewPage />} />
              <Route path="/w/:workspaceId/campaigns" element={<CampaignsPage />} />
              <Route path="/w/:workspaceId/ai-assistant" element={<AiAssistantPage />} />
              <Route path="/w/:workspaceId/campaigns/new" element={<CampaignBuilderPage />} />
              <Route
                path="/w/:workspaceId/campaigns/:campaignId"
                element={<CampaignDetailPage />}
              />
              <Route path="/w/:workspaceId/creative" element={<CreativePage />} />
              <Route path="/w/:workspaceId/localization" element={<LocalizationPage />} />
              <Route path="/w/:workspaceId/dashboard" element={<HomePage />} />
              <Route path="/w/:workspaceId/performance" element={<DashboardPage />} />
              <Route path="/w/:workspaceId/weekly-review" element={<WeeklyReviewPage />} />
              <Route path="/w/:workspaceId/experiments" element={<ExperimentsPage />} />
              <Route path="/w/:workspaceId/brand-safety" element={<BrandSafetyPage />} />
              <Route path="/w/:workspaceId/optimise" element={<OptimisePage />} />
              <Route path="/w/:workspaceId/attribution" element={<AttributionPage />} />
              <Route path="/w/:workspaceId/reports" element={<ReportsPage />} />
              <Route path="/w/:workspaceId/webhooks" element={<WebhooksPage />} />
              <Route path="/w/:workspaceId/channel-standards" element={<ChannelStandardsPage />} />
              <Route path="/w/:workspaceId/billing" element={<BillingPage />} />
              <Route path="*" element={<Navigate to="/workspaces" replace />} />
            </Routes>
          </AppShell>
        }
      />
    </Routes>
  );
}
